import type { PickActResult, PickOffer, PickOpened, PickPlayView, PlayPrize, RunPot } from '@goodtime/shared';

/**
 * Pick a prize, from the player's side:
 * - `choose`: the parcels are wrapped, waiting for a tap;
 * - `opening`: one was tapped and the server is asked to open it;
 * - `opened`: the server answered; only that parcel's prize is known.
 *
 * The server filled the parcels when it offered the play, so the tap only
 * reveals; every other parcel stays wrapped for good.
 */
export type PickPhase = 'choose' | 'opening' | 'opened';

export interface PickView {
  phase: PickPhase;
  boxes: number;
  /** The most a parcel holds (the offer's top prize). */
  maxValue: number;
  /** The parcel being opened, while the server answers. */
  busy: number | null;
  /** The opened parcel; null before, and when the play was settled
   *  without a pick (left for an hour). */
  opened: PickOpened | null;
  prize: PlayPrize | null;
  /** The run's pot after a lamp's game paid into it. */
  pot: RunPot | null;
  /** Paid into the run's pot rather than the wallet. */
  toPot: boolean;
  error: string | null;
}

export type PickEvent =
  | { type: 'tap'; box: number }
  | { type: 'answered'; result: PickActResult }
  /** The act failed: `play` is the server's view (from a 409, or fetched),
   *  or null when it couldn't be reached. */
  | { type: 'failed'; play: PickPlayView | null };

/** What the host should do: send the pick (always step 0, so a retry is
 *  answered once). */
export type PickEffect = { type: 'act'; step: 0; action: 'pick'; arg: string } | null;

const coinsOnly = (coins: number): PlayPrize => ({ coins, gems: 0, freeSpins: 0 });

export function pickStart(offer: PickOffer): PickView {
  return {
    phase: 'choose',
    boxes: offer.setup?.boxes ?? 3,
    maxValue: offer.maxValue,
    busy: null,
    opened: null,
    prize: null,
    pot: null,
    toPot: offer.creditTo === 'parent',
    error: null,
  };
}

/** The game as a pure reducer. A tap while one parcel is opening, or after
 *  one has opened, does nothing, so mashing sends one pick. */
export function reducePick(view: PickView, event: PickEvent): { view: PickView; effect: PickEffect } {
  switch (event.type) {
    case 'tap':
      if (view.phase !== 'choose' || event.box < 0 || event.box >= view.boxes) return { view, effect: null };
      return {
        view: { ...view, phase: 'opening', busy: event.box, error: null },
        effect: { type: 'act', step: 0, action: 'pick', arg: String(event.box) },
      };
    case 'answered': {
      const r = event.result;
      return {
        view: {
          ...view,
          phase: 'opened',
          busy: null,
          opened: r.reveal,
          prize: r.prize ?? coinsOnly(r.payout ?? r.reveal.coins),
          pot: r.pot ?? null,
          error: null,
        },
        effect: null,
      };
    }
    case 'failed': {
      const p = event.play;
      if (p && p.payout !== null) {
        // Already opened (a lost answer, another tab) or settled: show what
        // the server has, and pay nothing more.
        return {
          view: { ...view, phase: 'opened', busy: null, opened: p.state, prize: p.prize ?? coinsOnly(p.payout), error: null },
          effect: null,
        };
      }
      return {
        view: { ...view, phase: 'choose', busy: null, error: 'Lost the connection. Tap a parcel to try again.' },
        effect: null,
      };
    }
  }
}

/** The top prize came out. */
export const pickTop = (view: PickView): boolean => view.prize !== null && view.prize.coins >= view.maxValue;

/** The line under the title, said once: what to do, then what happened. */
export function pickLead(view: PickView): string {
  if (view.phase !== 'opened') return `Pick a parcel. One holds ${view.maxValue.toLocaleString('en-GB')} coins.`;
  return pickTop(view) ? 'The big one!' : 'A lovely surprise!';
}

/** Where the prize went, under the amount (a visitor's parcel raised to
 *  its floor says so). */
export const pickDestination = (view: PickView): string =>
  view.toPot ? 'Added to the pot' : view.prize?.floored ? 'Topped up as a thank-you, in your wallet' : 'Added to your wallet';
